import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  VStack,
  Heading,
  Text,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Select,
  Input,
  HStack,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { loadApplyFormData, saveApplyFormData } from "./applyFormStorage";
import { loadUserSession } from "./userSessionStorage";
import { hasCompletedBankVerification } from "./customerApplicationStatus";

export default function ApplyReapplyPage() {
  const navigate = useNavigate();

  const loanAmountOptions = [];
  for (let amount = 100; amount <= 1500; amount += 50) {
    loanAmountOptions.push(amount);
  }
  for (let amount = 2000; amount <= 20000; amount += 500) {
    loanAmountOptions.push(amount);
  }

  const storedSession = useMemo(() => loadUserSession(), []);
  const sessionAuth = storedSession.auth && typeof storedSession.auth === "object" ? storedSession.auth : {};
  const sessionCustomer = storedSession.customer && typeof storedSession.customer === "object" ? storedSession.customer : {};
  const isAuthenticated = Boolean(sessionAuth.isAuthenticated && (sessionAuth.customerId || sessionAuth.email));
  const canReapply = hasCompletedBankVerification(sessionCustomer);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/apply/sign-in", { replace: true });
      return;
    }

  }, [isAuthenticated, navigate]);

  const initialLoanAmount = useMemo(() => {
    const storedApply = loadApplyFormData();
    const applyLoanAmount =
      storedApply.preliminary && typeof storedApply.preliminary === "object"
        ? storedApply.preliminary.loanAmount
        : "";

    if (applyLoanAmount) {
      return String(applyLoanAmount);
    }

    const sessionLoanAmount = sessionCustomer.loanAmount;

    return sessionLoanAmount ? String(sessionLoanAmount) : "";
  }, [sessionCustomer.loanAmount]);

  const lockedProvince = useMemo(() => {
    const storedApply = loadApplyFormData();
    const profileProvince =
      sessionCustomer.personalInfo && typeof sessionCustomer.personalInfo === "object"
        ? sessionCustomer.personalInfo.province
        : "";
    const normalizedProfileProvince = typeof profileProvince === "string" ? profileProvince.trim() : "";

    if (normalizedProfileProvince) {
      return normalizedProfileProvince;
    }

    const applyProvince =
      storedApply.preliminary && typeof storedApply.preliminary === "object"
        ? storedApply.preliminary.province
        : "";

    if (applyProvince) {
      return String(applyProvince);
    }

    const sessionProvince = typeof sessionCustomer.province === "string" ? sessionCustomer.province.trim() : sessionCustomer.province;

    return sessionProvince ? String(sessionProvince) : "";
  }, [sessionCustomer.personalInfo, sessionCustomer.province]);

  const [loanAmount, setLoanAmount] = useState(initialLoanAmount);
  const [touched, setTouched] = useState(false);

  const error = touched && !loanAmount ? "Please select a loan amount" : "";

  const handleBack = () => {
    navigate("/profile");
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setTouched(true);

    if (!isAuthenticated) {
      navigate("/apply/sign-in");
      return;
    }

    if (!loanAmount) {
      return;
    }

    const existingData = loadApplyFormData();
    const existingPreliminary =
      existingData.preliminary && typeof existingData.preliminary === "object"
        ? existingData.preliminary
        : {};

    saveApplyFormData({
      preliminary: {
        ...existingPreliminary,
        loanAmount,
        province: lockedProvince || existingPreliminary.province || "",
      },
    });

    const existingFlow = existingData.flow && typeof existingData.flow === "object" ? existingData.flow : {};
    saveApplyFormData({
      flow: {
        ...existingFlow,
        incomeBackPath: "/apply/reapply",
        personalInfoBackPath: "/apply/reapply",
      },
    });

    if (!canReapply) {
      navigate("/apply/personal-info");
      return;
    }

    navigate("/apply/income-info");
  };

  return (
    <Box width="100%" bg="gray.50" minH="100vh" pt={{ base: 28, md: 32 }} pb={{ base: 12, md: 16 }} px={{ base: 4, md: 8 }}>
      <VStack maxW="760px" mx="auto" spacing={8} align="stretch">
        <Box border="1px solid" borderColor="gray.200" borderRadius="2xl" bg="white" boxShadow="sm" px={{ base: 5, md: 8 }} py={{ base: 7, md: 9 }}>
          <VStack spacing={5} align="stretch">
            <VStack spacing={2} align="stretch" textAlign="center">
              <Heading size="lg">{canReapply ? "Reapply Online" : "Apply Online"}</Heading>
              <Text color="gray.600" fontSize="sm">
                {canReapply
                  ? "Select your loan amount to continue your application from income details."
                  : "Select your loan amount and continue to complete your customer information."}
              </Text>
            </VStack>

            <form onSubmit={handleSubmit}>
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel fontSize="sm" color="gray.700">
                    Province
                  </FormLabel>
                  <Input
                    value={lockedProvince || "Not available"}
                    isReadOnly
                    bg="gray.50"
                    h="52px"
                    borderRadius="lg"
                    borderColor="gray.300"
                    _focus={{ borderColor: "gray.300", boxShadow: "none" }}
                  />
                </FormControl>

                <FormControl isRequired isInvalid={Boolean(error)}>
                  <FormLabel fontSize="sm" color="gray.700">
                    Loan amount
                  </FormLabel>
                  <Select
                    value={loanAmount}
                    onChange={(event) => setLoanAmount(event.target.value)}
                    onBlur={() => setTouched(true)}
                    bg="white"
                    h="52px"
                    borderRadius="lg"
                    borderColor="gray.300"
                    _focus={{ borderColor: "black", boxShadow: "none" }}
                    _hover={{ borderColor: "gray.400" }}
                    required
                  >
                    <option value="" disabled hidden />
                    {loanAmountOptions.map((amount) => (
                      <option key={amount} value={amount}>
                        {`$${amount.toLocaleString()}`}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{error}</FormErrorMessage>
                </FormControl>

                <HStack spacing={3} justify="space-between">
                  <Button
                    type="button"
                    variant="outline"
                    borderColor="gray.300"
                    color="gray.700"
                    borderRadius="full"
                    h="50px"
                    px={8}
                    _hover={{ bg: "gray.50" }}
                    onClick={handleBack}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    bg="black"
                    color="white"
                    borderRadius="full"
                    h="50px"
                    px={8}
                    _hover={{ bg: "gray.900" }}
                  >
                    Continue
                  </Button>
                </HStack>
              </VStack>
            </form>
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
}
